import Cookies from 'js-cookie';

const HEARTBEAT_INTERVAL = 4000;
const LEASE_TIMEOUT = 90000;
const TAB_ID_KEY = 'chatfree-dashboard-tab-id';
const CHANNEL_NAME = 'chatfree-dashboard-tabs';

const randomId = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const sessionFingerprint = () => {
  const rawAuthData = Cookies.get('cw_d_session_info');
  if (!rawAuthData) return null;

  try {
    const authData = JSON.parse(rawAuthData);
    return authData.client || authData.uid || rawAuthData;
  } catch {
    return rawAuthData;
  }
};

const hash = value => {
  let result = 0;
  for (let index = 0; index < value.length; index += 1) {
    result = (result * 31 + value.charCodeAt(index)) % 2147483647;
  }
  return result.toString(36);
};

const parseLease = value => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const createTabId = () => {
  try {
    const existingId = sessionStorage.getItem(TAB_ID_KEY);
    const id = existingId || randomId();
    sessionStorage.setItem(TAB_ID_KEY, id);
    return id;
  } catch {
    return randomId();
  }
};

class SingleTabCoordinator {
  constructor() {
    this.tabId = createTabId();
    this.fingerprint = sessionFingerprint();
    this.storageKey = this.fingerprint
      ? `chatfree-dashboard-leader:${hash(this.fingerprint)}`
      : null;
    this.channel = this.createChannel();
    this.heartbeat = null;
    this.storageAvailable = true;
    this.onStorage = this.onStorage.bind(this);
    this.onChannelMessage = this.onChannelMessage.bind(this);
    this.release = this.release.bind(this);
  }

  async acquire() {
    if (!this.storageKey) return true;

    try {
      const lease = this.readLease();
      if (this.isActiveOtherTab(lease)) return false;
      if (await this.probeForLeader()) return false;

      this.writeLease();
      await new Promise(resolve => {
        window.setTimeout(resolve, 100 + Math.floor(Math.random() * 100));
      });

      if (this.readLease()?.tabId !== this.tabId) return false;

      this.startLeadership();
      return true;
    } catch {
      this.storageAvailable = false;
      if (await this.probeForLeader()) return false;

      this.startLeadership();
      return true;
    }
  }

  takeOver() {
    if (!this.storageKey) return;
    try {
      this.writeLease();
    } catch {
      this.storageAvailable = false;
    }
    this.channel?.postMessage({ type: 'takeover', tabId: this.tabId });
    window.location.reload();
  }

  watchAvailability(callback) {
    if (!this.storageKey) return;

    let checking = false;
    const check = async () => {
      if (checking) return;
      const lease = this.readLease();
      if (lease && Date.now() - lease.timestamp <= LEASE_TIMEOUT) return;

      checking = true;
      const leaderPresent = await this.probeForLeader();
      checking = false;
      if (!leaderPresent) callback();
    };

    window.addEventListener('storage', check);
    this.channel?.addEventListener('message', check);
    window.setInterval(check, HEARTBEAT_INTERVAL);
  }

  startLeadership() {
    window.addEventListener('storage', this.onStorage);
    window.addEventListener('beforeunload', this.release);
    this.channel?.addEventListener('message', this.onChannelMessage);
    this.heartbeat = window.setInterval(() => {
      if (this.storageAvailable && this.readLease()?.tabId !== this.tabId) {
        this.handleLeadershipLost();
        return;
      }
      if (this.storageAvailable) this.writeLease();
      this.channel?.postMessage({ type: 'heartbeat', tabId: this.tabId });
    }, HEARTBEAT_INTERVAL);
  }

  onStorage(event) {
    if (event.key !== this.storageKey) return;
    const lease = parseLease(event.newValue);
    if (this.isActiveOtherTab(lease)) this.handleLeadershipLost();
  }

  onChannelMessage(event) {
    const { type, tabId, nonce } = event.data || {};
    if (type === 'probe') {
      this.channel?.postMessage({
        type: 'leader',
        tabId: this.tabId,
        nonce,
      });
    }
    if (type === 'takeover' && tabId !== this.tabId) {
      this.handleLeadershipLost();
    }
  }

  handleLeadershipLost() {
    if (this.heartbeat) window.clearInterval(this.heartbeat);
    window.location.reload();
  }

  release() {
    try {
      if (this.readLease()?.tabId === this.tabId) {
        localStorage.removeItem(this.storageKey);
        this.channel?.postMessage({ type: 'released', tabId: this.tabId });
      }
    } catch {
      // Storage can be unavailable in privacy-restricted browsers.
    }
  }

  writeLease() {
    localStorage.setItem(
      this.storageKey,
      JSON.stringify({ tabId: this.tabId, timestamp: Date.now() })
    );
  }

  readLease() {
    try {
      return parseLease(localStorage.getItem(this.storageKey));
    } catch {
      return null;
    }
  }

  probeForLeader() {
    if (!this.channel) return Promise.resolve(false);

    const nonce = randomId();
    return new Promise(resolve => {
      let timeout;
      let onMessage;
      const finish = leaderPresent => {
        window.clearTimeout(timeout);
        this.channel.removeEventListener('message', onMessage);
        resolve(leaderPresent);
      };
      onMessage = event => {
        if (
          event.data?.type === 'leader' &&
          event.data?.nonce === nonce &&
          event.data?.tabId !== this.tabId
        ) {
          finish(true);
        }
      };
      timeout = window.setTimeout(() => finish(false), 250);

      this.channel.addEventListener('message', onMessage);
      this.channel.postMessage({ type: 'probe', tabId: this.tabId, nonce });
    });
  }

  createChannel() {
    if (!this.storageKey || !('BroadcastChannel' in window)) return null;

    try {
      return new BroadcastChannel(`${CHANNEL_NAME}:${hash(this.fingerprint)}`);
    } catch {
      return null;
    }
  }

  isActiveOtherTab(lease) {
    return (
      lease?.tabId &&
      lease.tabId !== this.tabId &&
      Date.now() - lease.timestamp <= LEASE_TIMEOUT
    );
  }
}

export default new SingleTabCoordinator();
